import { isDefined } from '@/composables/utils'
import { EndPoint } from '@/enums/EndPoint'
import { TableStoreId } from '@/enums/TableStoreId'
import type { ICustomFieldsStore } from '@/interfaces/api/ICustomFieldsStore'
import type { IImportDefinition } from '@/interfaces/IImportDefinition'
import type { PeopleRecord } from '@/models/PeopleRecord'
import { TableStoreFactory } from '@/stores/db/TableStoreFactory'
import { useSettingsStore } from '@/stores/ui/SettingsStore'
import dayjs from 'dayjs'
import { useI18n } from 'vue-i18n'
import { displayDate, toBoolean, toISODate, toText } from './'

/**
 * Run before import to load the cache of custom field definitions.
 */
async function beforeImport() {
  const customFieldsStore = TableStoreFactory.get(TableStoreId.CustomFields) as ICustomFieldsStore
  await customFieldsStore.loadItems()
}

/**
 * Run after import to clear the cache of custom field definitions.
 */
async function afterImport() {
  const customFieldsStore = TableStoreFactory.get(TableStoreId.CustomFields) as ICustomFieldsStore
  customFieldsStore.items = []
  customFieldsStore.count = 0
}

/**
 * Add calculated fields to the people record.
 * @param item The people record to add calculated fields to.
 */
async function addCalculatedFields(item: PeopleRecord) {
  const customFieldsStore = TableStoreFactory.get(TableStoreId.CustomFields) as ICustomFieldsStore
  const settingsStore = useSettingsStore()

  customFieldsStore.updateCustomFields(item)

  const daysToExpire = settingsStore.globals.training.daysToExpire
  if (isDefined(item.trainingDate) && !isDefined(item.trainingExpiration)) {
    item.trainingExpiration = dayjs(item.trainingDate).add(daysToExpire, 'day').toISOString()
  }
}

export const getImportDefinition = (): IImportDefinition => {
  const i18n = useI18n({ useScope: 'global' })
  return {
    beforeImport,
    afterImport,
    addCalculatedFields,
    allowInsert: true,
    endpoint: EndPoint.People,
    keyFields: ['firstName', 'lastName', 'idNumber'],
    constraints: [['email']],
    ignoredFields: ['supervisorId'],
    friendlyName: 'tables.names.people',
    fields: [
      {
        title: i18n.t('fields.people.lastName'),
        name: 'lastName',
        convert: toText(64),
        required: true
      },
      {
        title: i18n.t('fields.people.firstName'),
        name: 'firstName',
        convert: toText(64),
        required: true
      },
      {
        title: i18n.t('fields.people.idNumber'),
        name: 'idNumber',
        convert: toText(64),
        required: true
      },
      {
        title: i18n.t('fields.people.company'),
        name: 'company',
        convert: toText(64)
      },
      {
        title: i18n.t('fields.people.location'),
        name: 'location',
        convert: toText(64)
      },
      {
        title: i18n.t('fields.people.note'),
        name: 'note',
        convert: toText(128)
      },
      {
        title: i18n.t('fields.people.custom1Label'),
        name: 'custom1Label',
        convert: toText(64)
      },
      {
        title: i18n.t('fields.people.custom1Data'),
        name: 'custom1Data',
        convert: toText(64)
      },
      {
        title: i18n.t('fields.people.custom2Label'),
        name: 'custom2Label',
        convert: toText(64)
      },
      {
        title: i18n.t('fields.people.custom2Data'),
        name: 'custom2Data',
        convert: toText(64)
      },
      {
        title: i18n.t('fields.people.custom3Label'),
        name: 'custom3Label',
        convert: toText(64)
      },
      {
        title: i18n.t('fields.people.custom3Data'),
        name: 'custom3Data',
        convert: toText(64)
      },
      {
        title: i18n.t('fields.people.custom4Label'),
        name: 'custom4Label',
        convert: toText(64)
      },
      {
        title: i18n.t('fields.people.custom4Data'),
        name: 'custom4Data',
        convert: toText(64)
      },
      {
        title: i18n.t('fields.people.inactive'),
        name: 'inactive',
        aliases: ['Person Inactive'],
        convert: toBoolean,
        defaultValue: false
      },
      {
        title: i18n.t('fields.people.email'),
        name: 'email',
        aliases: ['Email Address'],
        convert: toText(384)
      },
      {
        title: i18n.t('fields.people.respiratorRequired'),
        name: 'respiratorRequired',
        convert: toBoolean,
        defaultValue: true,
        aliases: ['Is a respirator required', 'Is respirator required']
      },
      {
        title: i18n.t('fields.people.trainingDate'),
        name: 'trainingDate',
        aliases: ['Last Training', 'Last Training Date'],
        convert: toISODate,
        display: displayDate
      },
      {
        title: i18n.t('fields.people.trainingStatus'),
        name: 'trainingExpiration',
        aliases: [
          'Training Expires',
          'Training Status',
          'Respirator Training Expires',
          'Respirator Training Status'
        ],
        convert: toISODate,
        display: displayDate
      },
      {
        title: i18n.t('fields.people.isSupervisor'),
        name: 'isSupervisor',
        convert: toBoolean,
        defaultValue: false,
        aliases: ['Is a supervisor']
      }
    ]
  }
}
